import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import { type Condition, CONDITIONS } from 'types/releaseTask';
import {
  REGISTRATION_STEP,
  RegistrationStep,
} from '../../../../constants/registrationStep';

/**
 * リリースタスク登録画面の状態
 */
interface RegistrationState {
  /** 登録モーダル表示フラグ */
  isOpen: boolean;
  /** 登録ステップ */
  step: RegistrationStep;
  condition: Condition;
  targetGroupList: string[];
  targetGroup: string;
}

/**
 * リリースタスク登録画面の初期状態
 */
const initialState: RegistrationState = {
  step: REGISTRATION_STEP.INPUT,
  isOpen: false,
  condition: CONDITIONS.ALL,
  targetGroupList: [],
  targetGroup: '',
};

/**
 * リリースタスク登録画面の slice(reducer + action)
 */
export const registrationSlice = createSlice({
  name: 'registrationTask',
  initialState,
  reducers: {
    initialize: () => initialState,
    modalOpened: (state) => {
      state.isOpen = true;
    },
    updateStep: (
      state,
      { payload }: PayloadAction<RegistrationState['step']>,
    ) => ({
      ...state,
      step: payload,
    }),
    conditionUpdated: (
      state,
      action: PayloadAction<{ condition: Condition }>,
    ) => {
      state.condition = action.payload.condition;
    },
    /**
     * リリースタスクの新規・変更登録画面で選択した`ターゲットグループ`を保存します。
     */
    updateTargetGroup: (
      state,
      action: PayloadAction<{ targetGroup: string }>,
    ) => {
      state.targetGroup = action.payload.targetGroup;
    },
  },
});

/**
 * リリースタスク登録画面の action
 */
export const registrationActions = registrationSlice.actions;

const getReleaseEnvironments = (state: RootState) =>
  state.releaseEnvironment.list.releaseEnvironments;

/**
 * リリースタスク登録画面の selectors
 */
export const registrationSelectors = {
  isOpenModal: (state: RootState) => state.releaseTask.registration.isOpen,
  currentModalStep: (state: RootState) => state.releaseTask.registration.step,
  // 顧客一覧表示フラグ
  shouldShowCustomerList: (state: RootState) =>
    state.releaseTask.registration.condition !== CONDITIONS.ALL,
  condition: (state: RootState) => state.releaseTask.registration.condition,
  targetGroupList: (state: RootState) => state.releaseTask.list.targetGroupList,
  targetGroup: (state: RootState) => state.releaseTask.registration.targetGroup,
  releaseEnvironments: getReleaseEnvironments,
} as const;
